import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SkeletonScreenModule } from '@daysmart/angular-skeleton-screen';
import { IntlModule, IntlService } from '@progress/kendo-angular-intl';
import '@progress/kendo-angular-intl/locales/en/all';
import { ServiceApiService } from '@services/api/service-api.service';
import { NavigationService } from '@services/navigation.service';
import { SharedDirectivesAndPipesModule } from 'app-classic/shared-directives-and-pipe.module';
import player from 'lottie-web';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import { LottieComponent, provideLottieOptions } from 'ngx-lottie';
import { NgxMaskModule } from 'ngx-mask';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppSpartanUiModule } from './app.spartan-ui-module';
import { BookingFlowComponent } from './components/booking-flow/booking-flow.component';
import { AddPetComponent } from './components/booking-steps/add-pet/add-pet.component';
import { AddServiceComponent } from './components/booking-steps/add-service/add-service.component';
import { EnterYourInformationComponent } from './components/booking-steps/enter-your-information/enter-your-information.component';
import { PetInformationComponent } from './components/booking-steps/pet-information/pet-information.component';
import { PetListComponent } from './components/booking-steps/pet-list/pet-list.component';
import { ReviewRequestComponent } from './components/booking-steps/review-request/review-request.component';
import { SelectDateTimeComponent } from './components/booking-steps/select-date-time/select-date-time.component';
import { SelectEmployeeComponent } from './components/booking-steps/select-employee/select-employee.component';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationComponent } from './components/confirmation/confirmation.component';
import { GeneralDialogComponent } from './components/dialog/general-dialog.component';
import { ErrorComponent } from './components/error/error.component';
import { TitleBarComponent } from './components/title-bar/title-bar.component';
import { CreateAccountFormComponent } from './components/user-accounts/create-account-form/create-account-form.component';
import { LoginFormComponent } from './components/user-accounts/login-form/login-form.component';
import { DsbCurrencyPipe } from './pipes/dsb-currency.pipe';
import { DurationPipe } from './pipes/duration.pipe';
import { AppSyncService } from './services/app-sync.service';
import { CheckTokensService } from './services/check-tokens.service';
import { ConfigService } from './services/config/config.service';
import { APP_CONFIG } from './services/config/constants';
import { LocaleService } from './services/locale.service';
import { ThemeService } from './services/theme.service';

@NgModule({
    declarations: [
        AppComponent,
        TitleBarComponent,
        BookingFlowComponent,
        AddServiceComponent,
        SelectEmployeeComponent,
        EnterYourInformationComponent,
        ReviewRequestComponent,
        ConfirmationComponent,
        PetInformationComponent,
        ErrorComponent,
        //HorizontalScrollDirective, //when app-classic is deleted and SharedDirectivesAndPipesModule is removed, re-add this directive and SafePipe directly
        SelectDateTimeComponent,
        LoginFormComponent,
        //SafePipe,
        CreateAccountFormComponent,
        ConfirmationDialogComponent,
        GeneralDialogComponent,
        PetListComponent,
        AddPetComponent,
        DurationPipe,
        DsbCurrencyPipe,
    ],
    bootstrap: [AppComponent],
    imports: [
        LottieComponent,
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        AppSpartanUiModule,
        FormsModule,
        ReactiveFormsModule,
        NgxMaskModule.forRoot(),
        IntlModule,
        RecaptchaV3Module,
        SharedDirectivesAndPipesModule,
        SkeletonScreenModule,
    ],
    providers: [
        ConfigService,
        ThemeService,
        NavigationService,
        ServiceApiService,
        CheckTokensService,
        AppSyncService,
        provideAppInitializer(() => {
            const initializerFn = ((themingService: ThemeService) => () => {
                return themingService.initialize();
            })(inject(ThemeService));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = ((checkTokensService: CheckTokensService) => () => {
                return checkTokensService.performCheck();
            })(inject(CheckTokensService));
            return initializerFn();
        }),
        {
            provide: IntlService,
            useExisting: LocaleService,
        },
        {
            provide: RECAPTCHA_V3_SITE_KEY,
            useFactory: (configService: ConfigService) => {
                return configService.config.reCaptchaSiteKey;
            },
            deps: [ConfigService, APP_CONFIG],
        },
        provideHttpClient(withInterceptorsFromDi()),
        provideLottieOptions({
            player: () => player,
        }),
    ],
})
export class AppModule {}
